import dayjs from "dayjs";

export type ElapsedTimeTranslations = {
  lessThanAnHourAgo: string;
  oneHourAgo: string;
  hoursAgo: string;
  yesterday: string;
  daysAgo: string;
  oneMonthAgo: string;
  monthsAgo: string;
  oneYearAgo: string;
  yearsAgo: string;
};

export const formatElapsedTime = (
  date: Date,
  elapsedTime: ElapsedTimeTranslations,
): string => {
  const diffMs = Date.now() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffHours < 1) return elapsedTime.lessThanAnHourAgo;
  if (diffHours < 24) {
    return diffHours === 1
      ? elapsedTime.oneHourAgo
      : elapsedTime.hoursAgo.replace("{{count}}", String(diffHours));
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return elapsedTime.yesterday;

  // Calendar month/year diffing (not a flat 30-day division) so a click on
  // 31 Jan reads as "1 month ago" on 1 Mar, matching the varying month
  // lengths ("30 days/31 days/28 days, whichever is applicable") the AC
  // scale is based on. Must run before falling back to a day count: diffDays
  // alone can't tell a same-month gap from a cross-month one (e.g. 31 Jan to
  // 1 Mar is only 29 days but already a full calendar month).
  const diffMonths = dayjs().diff(dayjs(date), "month");
  if (diffMonths < 1) {
    return elapsedTime.daysAgo.replace("{{count}}", String(diffDays));
  }
  if (diffMonths < 12) {
    return diffMonths === 1
      ? elapsedTime.oneMonthAgo
      : elapsedTime.monthsAgo.replace("{{count}}", String(diffMonths));
  }

  const diffYears = Math.max(1, dayjs().diff(dayjs(date), "year"));
  return diffYears === 1
    ? elapsedTime.oneYearAgo
    : elapsedTime.yearsAgo.replace("{{count}}", String(diffYears));
};

// AC-1: the hover tooltip with the exact click date must not be shown when
// the click happened today (viewer-local calendar day).
export const isToday = (date: Date): boolean =>
  dayjs(date).isSame(dayjs(), "day");
